import React, { useState } from "react";
import { useAllOrdersContext } from "../../hooks/useContext/useAllOrdersContext";
import { useAuthContext } from "../../hooks/useContext/useAuthContext";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { CircularProgress, Typography } from "@mui/material";
import ErrorBox from "../Shared/ErrorBox";
import { Tabs, Tab } from '@mui/material';
import { useTheme } from "@mui/material/styles";


const OrdersComponentController = () => {
  const theme = useTheme();
  const { user } = useAuthContext();
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState('pending');
  const { allOrders, allOrdersDispatch } = useAllOrdersContext();
  const pendingOrders = allOrders ? allOrders.filter(order => order.status === 'Pending') : [];
  const [loadingStates, setLoadingStates] = useState({});

  const handleOrderListChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const updateOrderStatus = async (order) => {
    setError(null);
    setLoadingStates(prevStates => ({ ...prevStates, [order._id]: true }));
    const updatedOrderInfo = { ...order, status: 'Complete' };
    try {
      const response = await fetch(process.env.REACT_APP_SERVER_URL + "/api/orders/pendingOrders", {
        method: "PUT",
        body: JSON.stringify(updatedOrderInfo),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`,
        },
      });
      const json = await response.json();
      if (response.ok) {
        setLoadingStates(prevStates => ({ ...prevStates, [order._id]: false }));
        allOrdersDispatch({ type: "UPDATE_ORDERS", payload: json });
      }
    } catch (error) {
      setError(error);
      setLoadingStates(prevStates => ({ ...prevStates, [order._id]: false }));
    }
  };

// Display loading state or similar message
  if (!allOrders) {
    return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: '10vh' }}>
      <CircularProgress />
    </Box>
    );
  };

  return (
      <Box sx={{ "& > :not(style)": { m: 1, width: "100%" }, display: "flex", flexDirection: "column", alignItems: "center", p: 2 }}>
      <Tabs value={tabValue} onChange={handleOrderListChange} centered>
        <Tab value={'pending'} label={`Nowe zamówienia (${pendingOrders.length})`} wrapped />
        <Tab value="all" label={`Wszystkie zamówienia (${allOrders.length})`} wrapped />
      </Tabs>

      {tabValue === 'pending' && pendingOrders.map((order) => (
        <div key={order._id} style={{
            margin: '20px',
            padding: '20px',
            border: '1px solid #ddd',
            borderRadius: '12px',
            backgroundColor: '#fafafa',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <Typography variant="h6" component="p" style={{ fontWeight: 'bold', marginBottom: '10px', color: '#333' }}>
                Czas Zamówienia: {new Date(order.createdAt).toLocaleString('en-GB', { 
                  day: '2-digit', 
                  month: '2-digit', 
                  year: 'numeric', 
                  hourCycle: 'h23', 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </Typography>
              <Typography variant="subtitle1" component="p" style={{ marginBottom: '8px', color: '#555' }}>
                <strong>Imię i Nazwisko:</strong> {order.name}
              </Typography>
              <Typography variant="subtitle1" component="p" style={{ marginBottom: '8px', color: '#555' }}>
                <strong>Email:</strong> {order.email}
              </Typography>
              <Typography variant="subtitle1" component="p" style={{ marginBottom: '16px', color: '#555' }}>
                <strong>Status Płatności:</strong> {order.paidStatus ? 'Zapłacone' : 'Niezapłacone'}
              </Typography>
              <Typography variant="subtitle1" component="p" style={{ marginBottom: '16px', fontWeight: 'bold', color: '#333' }}>
                <strong>Całkowita Cena:</strong> {`${order.totalPrice} zł`}
              </Typography>
            </div>
            <div style={{ flex: 1, borderLeft: '1px solid #ddd', paddingLeft: '20px' }}>
              <ul style={{ paddingLeft: '20px', marginBottom: '16px', color: '#444' }}>
                {order.orderItems.map((oi, index) => (
                  <li key={index} style={{ marginBottom: '12px' }}>
                    <Typography variant="body1" component="p" style={{ fontWeight: 'bold' }}>
                      {oi.itemName} - Ilość: {oi.itemQuantity}
                    </Typography>

                    {oi.itemSize !== 'Jeden rozmiar' && (
                      <Typography variant="body2" component="p" style={{ fontStyle: 'italic', color: '#777' }}>
                        Rozmiar: {oi.itemSize}
                      </Typography>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          {loadingStates[order._id] && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", padding: '1%' }}>
              <CircularProgress />
            </Box>
          )}
          { error && 
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", padding: '1%' }}>
              <ErrorBox error={error} color="error" />
            </Box>
          }
          <Button variant="contained" type="button" disabled={loadingStates[order._id]} onClick={() => updateOrderStatus(order)}
            sx={{
              cursor: 'pointer',
              color: theme.palette.common.white,
              backgroundColor: theme.palette.primary.main,
              ':hover': { bgcolor: theme.palette.secondary.main },
              marginTop: '16px',
              borderRadius: '8px',
              padding: '8px 20px',
              textTransform: 'none',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
            }}
          >
            Oznacz jako gotowe
          </Button>
        </div>
      ))}
      {tabValue === 'all' && allOrders.map((order) => (
        <div key={order._id} style={{
            margin: '20px',
            padding: '20px',
            border: '1px solid #ddd',
            borderRadius: '12px',
            backgroundColor: '#fafafa',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <Typography variant="h6" component="p" style={{ fontWeight: 'bold', marginBottom: '10px', color: '#333' }}>
                Czas Zamówienia: {new Date(order.createdAt).toLocaleString('en-GB', { 
                  day: '2-digit', 
                  month: '2-digit', 
                  year: 'numeric', 
                  hourCycle: 'h23', 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </Typography>
              <Typography variant="subtitle1" component="p" style={{ marginBottom: '8px', color: '#555' }}>
                <strong>Imię i Nazwisko:</strong> {order.name}
              </Typography>
              <Typography variant="subtitle1" component="p" style={{ marginBottom: '8px', color: '#555' }}>
                <strong>Email:</strong> {order.email}
              </Typography>
              <Typography variant="subtitle1" component="p" style={{ marginBottom: '16px', color: '#555' }}>
                <strong>Status Płatności:</strong> {order.paidStatus ? 'Zapłacone' : 'Niezapłacone'}
              </Typography>
              <Typography variant="subtitle1" component="p" style={{ marginBottom: '16px', fontWeight: 'bold', color: '#333' }}>
                <strong>Całkowita Cena:</strong> {`${order.totalPrice} zł`}
              </Typography>
            </div>
            <div style={{ flex: 1, borderLeft: '1px solid #ddd', paddingLeft: '20px' }}>
              <ul style={{ paddingLeft: '20px', marginBottom: '16px', color: '#444' }}>
                {order.orderItems.map((oi, index) => (
                  <li key={index} style={{ marginBottom: '12px' }}>
                    <Typography variant="body1" component="p" style={{ fontWeight: 'bold' }}>
                      {oi.itemName} - Ilość: {oi.itemQuantity}
                    </Typography>

                    {oi.itemSize !== 'Jeden rozmiar' && (
                      <Typography variant="body2" component="p" style={{ fontStyle: 'italic', color: '#777' }}>
                        Rozmiar: {oi.itemSize}
                      </Typography>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <Box sx={{display: "flex", justifyContent: "center"}}>
            <Typography variant="subtitle1" component="p">{`Status: ${order.status === 'Complete' ? 'Zakończone' : 'Oczekujące'}`}</Typography>
          </Box>
        </div>
      ))}
    </Box>
  );
};


export default OrdersComponentController;